import React, { useEffect, useMemo } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import Header from "../Header/Header";
import Footer from "../Footer/Footer";

import MainScreen from "./MainScreen";
import PrivacyPolicy from "../Header/Menus/PrivacyPolicy";
import TermsOfService from "../Header/Menus/TermsOfService";
import AuthAction from "../Authentication/AuthAction/AuthAction";
import Profile from "../Authentication/Login/Profile";
import CoursePage from "./Courses/CoursePage";
import BooksPage from "./Books/BooksPage";
import BookViewPage from "./Books/BookViewPage";
import Contact from "../Header/Menus/Contact";

import "./MainStackStyles.css";

const normalizePathname = (pathname: string): string => {
  const cleanPathname = String(pathname || "/")
    .trim()
    .toLowerCase();

  if (!cleanPathname || cleanPathname === "/") {
    return "/";
  }

  return cleanPathname.replace(/\/+$/, "");
};

const getRouteKey = (pathname: string, search: string): string => {
  const cleanPathname = normalizePathname(pathname);
  const cleanSearch = String(search || "").trim();

  if (cleanPathname === "/") {
    return "startseite";
  }

  if (cleanPathname === "/courses") {
    return "kurse";
  }

  if (cleanPathname === "/books") {
    return "buecher";
  }

  if (cleanPathname === "/book-view") {
    return `buch-ansicht-${cleanSearch || "default"}`.replace(
      /[^a-zA-Z0-9_-]+/g,
      "-",
    );
  }

  if (cleanPathname === "/contact") {
    return "kontakt";
  }

  if (cleanPathname === "/profile") {
    return "profil";
  }

  if (cleanPathname === "/auth/action") {
    return `auth-action-${cleanSearch || "default"}`.replace(
      /[^a-zA-Z0-9_-]+/g,
      "-",
    );
  }

  if (cleanPathname === "/privacy-policy") {
    return "datenschutz";
  }

  if (cleanPathname === "/terms-of-service") {
    return "nutzungsbedingungen";
  }

  return cleanPathname.replace(/[^a-z0-9]+/g, "-") || "seite";
};

const clearPageClasses = (): void => {
  const classesToRemove = [
    "main-screen-page",
    "course-screen-page",
    "courses-screen-page",
    "books-screen-page",
    "book-view-screen-page",
    "contact-screen-page",
    "privacy-screen-page",
    "terms-screen-page",
    "content-screen-page",
    "profile-screen-page",
  ];

  document.documentElement.classList.remove(...classesToRemove);
  document.body.classList.remove(...classesToRemove);
};

const MainStack: React.FC = () => {
  const location = useLocation();

  const cleanPathname = useMemo(() => {
    return normalizePathname(location.pathname);
  }, [location.pathname]);

  const routeKey = useMemo(() => {
    return getRouteKey(location.pathname, location.search);
  }, [location.pathname, location.search]);

  const renderKey = useMemo(() => {
    return `${routeKey}-${location.key || "direct"}`;
  }, [location.key, routeKey]);

  useEffect(() => {
    clearPageClasses();

    if (cleanPathname === "/") {
      document.documentElement.classList.add("main-screen-page");
      document.body.classList.add("main-screen-page");
    }

    if (cleanPathname === "/courses") {
      document.documentElement.classList.add("course-screen-page");
      document.body.classList.add("course-screen-page");
    }

    if (cleanPathname === "/books") {
      document.documentElement.classList.add("books-screen-page");
      document.body.classList.add("books-screen-page");
    }

    if (cleanPathname === "/book-view") {
      document.documentElement.classList.add("book-view-screen-page");
      document.body.classList.add("book-view-screen-page");
    }

    if (cleanPathname === "/contact") {
      document.documentElement.classList.add("contact-screen-page");
      document.body.classList.add("contact-screen-page");
    }

    if (cleanPathname === "/profile") {
      document.documentElement.classList.add("profile-screen-page");
      document.body.classList.add("profile-screen-page");
    }

    if (cleanPathname === "/privacy-policy") {
      document.documentElement.classList.add("privacy-screen-page");
      document.body.classList.add("privacy-screen-page");
    }

    if (cleanPathname === "/terms-of-service") {
      document.documentElement.classList.add("terms-screen-page");
      document.body.classList.add("terms-screen-page");
    }

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });

    window.dispatchEvent(
      new CustomEvent("dear-language-route-change", {
        detail: {
          pathname: cleanPathname,
          search: location.search,
          routeKey,
        },
      }),
    );

    if (cleanPathname === "/courses") {
      window.dispatchEvent(new CustomEvent("dear-language-course-reset"));
      window.dispatchEvent(new CustomEvent("dear-language-course-page-opened"));
    }

    if (cleanPathname === "/books") {
      window.dispatchEvent(new CustomEvent("dear-language-books-page-opened"));
    }

    if (cleanPathname === "/contact") {
      window.dispatchEvent(new CustomEvent("dear-language-contact-page-opened"));
    }

    if (cleanPathname === "/profile") {
      window.dispatchEvent(new CustomEvent("dear-language-profile-page-opened"));
    }

    return () => {
      clearPageClasses();
    };
  }, [cleanPathname, location.search, routeKey]);

  return (
    <div
      className={`mainWrapper main-route-${routeKey}`}
      data-current-route={cleanPathname}
    >
      <Header />

      <main
        key={renderKey}
        className="mainRouteContent"
        data-route={cleanPathname}
      >
        <Routes location={location} key={renderKey}>
          <Route path="/" element={<MainScreen />} />
          <Route path="/courses" element={<CoursePage />} />
          <Route path="/books" element={<BooksPage />} />
          <Route path="/book-view" element={<BookViewPage />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/auth/action" element={<AuthAction />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      <Footer />
    </div>
  );
};

export default MainStack;